import { useCallback, useEffect, useState } from 'react';
import { gymsService } from '../services/gyms';

/**
 * The names a gym has queued up for its next climbs.
 *
 * A gym names its problems from a list somebody wrote in advance — a wall reset
 * puts up thirty of them in an afternoon, and thinking of thirty names at the
 * wall is not the job. The list lives on the gym (`climb_name_suggestions`, one
 * name per line) rather than in the browser, so it is the same list for whoever
 * is setting: the scans page has always worked this way, and the climb editor
 * draws from the same pot.
 *
 * A name that gets used is **taken off the list**. That is what makes it a queue
 * rather than a bank of ideas: the next problem should not be offered the name
 * the last one just took.
 */
export function useGymNameSuggestions(gymSlug: string | undefined) {
  const [names, setNames] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!gymSlug) return;
    let cancelled = false;
    setLoading(true);
    gymsService
      .getGymBySlug(gymSlug)
      .then((gym) => {
        if (!cancelled) setNames(parseNames(gym.climb_name_suggestions));
      })
      // A gym that will not answer costs the setter a suggestion, not the form.
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [gymSlug]);

  /** One name from the list, never the one already in the field. */
  const draw = useCallback(
    (current: string) => {
      const available = names.filter((name) => name !== current.trim());
      if (available.length === 0) return null;

      return available[Math.floor(Math.random() * available.length)];
    },
    [names]
  );

  const save = useCallback(
    async (text: string) => {
      const next = parseNames(text);
      setNames(next);
      if (!gymSlug) return;

      await gymsService.updateGym(gymSlug, {
        climb_name_suggestions: next.join('\n') || undefined,
      });
    },
    [gymSlug]
  );

  /**
   * Strike a name off, because a climb now has it. Optimistic and unawaited by
   * design: the climb is already up, and a list that failed to save is a name
   * offered twice rather than anything the setter has lost.
   */
  const consume = useCallback(
    (used: string) => {
      const name = used.trim();
      // Read the list rather than update through it: a state updater may be run
      // more than once, and this one would send the request every time.
      if (!names.includes(name)) return;

      const next = names.filter((candidate) => candidate !== name);
      setNames(next);
      if (!gymSlug) return;

      void Promise.resolve(
        gymsService.updateGym(gymSlug, { climb_name_suggestions: next.join('\n') || undefined })
      ).catch(() => {});
    },
    [names, gymSlug]
  );

  return { names, loading, draw, save, consume, text: names.join('\n') };
}

function parseNames(raw: string | null | undefined): string[] {
  return (raw || '')
    .split('\n')
    .map((name) => name.trim())
    .filter(Boolean);
}
